/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import {
    DirectionalHint,
    IContextualMenuItem,
    IContextualMenuItemProps,
    IContextualMenuItemStyleProps,
    IContextualMenuItemStyles,
    IContextualMenuRenderItem,
    IContextualMenuStyleProps,
    IContextualMenuStyles
} from '@fluentui/react/lib/ContextualMenu';
import { Icon } from '@fluentui/react/lib/Icon';
import { classNamesFunction } from '@fluentui/react/lib/Utilities';
import * as React from 'react';
import { useState } from 'react';
import { defineMessages } from 'react-intl';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { getScopedTheme, getStylist } from 'src/core/themes/luisStylist';
import { LuisConstants } from 'src/core/utils/LuisConstants';
import { handleMenuListRender } from 'src/luis/modules/labeling/components/handleMenuListRender';
import { EntityDropdownSelection } from 'src/luis/modules/labeling/types/EntityDropdownProps';
import { generateDivider } from 'src/luis/modules/labeling/utils/entityDropdown/getMenuActionItems';
import { getNotFoundItem } from 'src/luis/modules/labeling/utils/entityDropdown/getNotFoundItem';
import { MenuActionItems } from 'src/luis/modules/labeling/utils/entityDropdown/menuActionsGenerator';
import { Entity } from 'src/luis/projects/entities/types/Entity';
import { ListEntity } from 'src/luis/projects/entities/types/ListEntity';
import { SimpleEntity } from 'src/luis/projects/entities/types/SimpleEntity';
import { isMachineLearned, prettyPrintEntity } from 'src/luis/projects/entities/utils/entityHelpers';
import { ModelType } from 'src/luis/types/ModelType';
import { LuisId } from 'src/luis/types/Resource';
import { Label } from 'src/modules/labeling/types/Label';

/**
 * Passed in params to the menuEntitiesGenerator.
 */
export type EntitiesGeneratorParams = {
    /**
     * A list of needed callback functions to add to the entities' in their IContextualMenuItem format.
     */
    callbackFunctions: {
        /**
         * A callback function to submit the givin entity.
         */
        onSubmit: (entity: Entity) => void;

        /**
         * A callback function to un-label the givin entity.
         */
        onUnLabel: (entities: Entity[]) => void;

        /**
         * A callback function to add the token to the synonymous to a sub-list of the givin sub-list entity.
         */
        onSubmitSublist: (entity: ListEntity, sublistName: string) => void;

        /**
         * A callback creates a new sub-list with the givin name for a list entity having the givin id.
         */
        onCreateSublist: (id: string, name: string) => void;

        /**
         * A callback function for creating a new ML child entity.
         */
        onCreateChild: (entityName: string, parentId: LuisId) => void;

        /**
         * A callback fired on the menu dismisses.
         */
        onDismiss: (e?: React.MouseEvent<HTMLElement>) => void;
    };

    /**
     * Localization function to generate localized text
     */
    localize: ReturnType<typeof useLocalization>['localize'];

    /**
     * The application's list of entities.
     */
    entities: Entity[];

    /**
     * The user selected entity.
     */
    entitySelection: EntityDropdownSelection;

    /**
     * An object with all actions.
     */
    actions: MenuActionItems;

    /**
     * The clicked on label if exists.
     */
    clickedOnLabel: Label;
};

const messages = defineMessages({
    sublistSearchPlaceholder: {
        id: 'labeling.entityDropdown.sublistSearchPlaceholder',
        defaultMessage: 'Search for or create a sublist'
    },
    emptyListItemsPlaceholder: {
        id: 'labeling.entityDropdown.emptyListItemsPlaceholder',
        defaultMessage: 'Enter a list item and create new one'
    },
    createNewSublist: {
        id: 'labeling.entityDropdown.createNewSublist',
        defaultMessage: 'Create new sublist'
    },
    createNewChild: {
        id: 'labeling.entityDropdown.createNewChild',
        defaultMessage: 'Create new child'
    },
    entitySearchPlaceholder: {
        id: 'labeling.entityDropdown.entitySearchPlaceholder',
        defaultMessage: 'Enter an entity name'
    },
    noItemsFound: {
        id: 'labeling.entityDropdown.noItemsFound',
        defaultMessage: 'No items found'
    },
    checked: {
        id: 'labeling.entityDropdown.checked',
        defaultMessage: 'checked'
    }
});

const dropdownTheme = getScopedTheme('EntityDropdown');
const { getClassName } = getStylist('EntityDropdown');

const checkBoxClassName = getClassName('checkBox', theme => ({
    opacity: 0,
    margin: '0px 2px',
    $nest: {
        '&:hover': {
            color: theme.blackColor,
            opacity: 1
        }
    }
}));
const checkMarkClassName = getClassName('checkMark', theme => ({
    color: theme.blackColor,
    margin: '0px 2px'
}));

const subMenuStyles = classNamesFunction<IContextualMenuStyleProps, IContextualMenuStyles>()({
    root: {
        boxShadow: dropdownTheme.menuBoxShadow,
        width: 270
    }
});
const baseItemStyles = classNamesFunction<IContextualMenuItemStyleProps, IContextualMenuItemStyles>()({
    secondaryText: {
        paddingLeft: 0,
        flexGrow: 1,
        textAlign: dropdownTheme.secondaryTextAlign,
        fontStyle: 'italic'
    },
    label: { flexGrow: 0 }
});

const ToggleCheckBoxIcon = ({
    onClick,
    isChecked
}: {
    onClick: (event: React.MouseEvent<HTMLElement, MouseEvent>) => void;
    isChecked: boolean;
}) => {
    const [isHovering, setIsHovering] = useState(false);
    const iconName = isHovering ? (isChecked ? 'CheckboxComposite' : 'CheckBox') : 'CheckMark';
    const className = isChecked ? checkMarkClassName : checkBoxClassName;

    return (
        <Icon
            onClick={onClick}
            iconName={iconName}
            className={className}
            data-automation-id="checkboxIcon"
            onMouseEnter={() => setIsHovering(true)}
            onMouseLeave={() => setIsHovering(false)}
        />
    );
};

// Return an array of label ancestors
const getAncestors = (label: Label): Label[] => {
    if (!label) {
        return [];
    }

    return [label].concat(getAncestors(label.parent));
};

const getItemsToOpen = (clickedOnLabel: Label, entitySelection: EntityDropdownSelection, topLevelEntities: Entity[]) => {
    if (clickedOnLabel) {
        // Menu items that should be opened when click on label .
        return getAncestors(clickedOnLabel?.parent).map(l => l.entity.id);
    }
        // The dropdown cascading menus should open to the token direct parent menu.
        // It shouldn't open if the token is labeled by more than one ML entity
        const selectionItemsToOpen = entitySelection?.selection.filter(e => e.typeId === ModelType.Simple).map(e => e.id);
        const mlEntitiesTopLevelItemsToOpen = topLevelEntities?.filter(
            e => e.typeId === ModelType.Simple && selectionItemsToOpen?.find(id => id === e.id)
        );

        return mlEntitiesTopLevelItemsToOpen.length === 1 ? selectionItemsToOpen : [];
};

/**
 * Returns a list of filtered entities tailored for the entities menu display.
 */
export const menuEntitiesGenerator = (params: EntitiesGeneratorParams) => {
    const { callbackFunctions, entities, entitySelection, actions, clickedOnLabel, localize } = params;
    const { onSubmit, onUnLabel, onSubmitSublist, onDismiss, onCreateSublist, onCreateChild } = callbackFunctions;

    const itemsToOpen = React.useMemo(() => getItemsToOpen(clickedOnLabel, entitySelection, entities), [
        clickedOnLabel,
        entitySelection,
        entities
    ]);

    // A helper function determines whether the given entity is labeled or not
    const isEntityChecked = React.useCallback(
        (entity: Entity) => {
            const isChecked = entitySelection?.kind === 'label'
                && Boolean(entitySelection.selection.find(selectedEntity => selectedEntity.id === entity.id));

            if (isChecked) {
                return true;
            }

            // Label hierarchical parent entity as checked in case of hierarchical entity child selected.
            if (clickedOnLabel?.entity.typeId === ModelType.HierarchicalChild) {
                return entity.name === clickedOnLabel.entity.name.split('::')[0];
            } if (entity.typeId === ModelType.Hierarchical) {
                return Boolean(entitySelection?.selection.find(s => s.name.split('::')[0] === entity.name));
            }

            return false;
        },
        [entitySelection, clickedOnLabel]
    );

    const handleSubmit = (_: React.MouseEvent | React.KeyboardEvent, item: IContextualMenuItem) => {
        onSubmit(item.data as Entity);
    };

    const handleUnLabel = (_: React.MouseEvent | React.KeyboardEvent, item: IContextualMenuItem) => {
        onUnLabel([item.data as Entity]);
    };

    const handleOnItemClick = (ev: React.MouseEvent | React.KeyboardEvent, item: IContextualMenuItem, dismiss: boolean = true) => {
        if (isEntityChecked(item.data as Entity)) {
            handleUnLabel(ev, item);
        } else {
            handleSubmit(ev, item);
        }
        if (dismiss) {
            onDismiss(ev as React.MouseEvent<HTMLElement, MouseEvent>);
        }
    };

    const handleSubEntityClick = (ev: React.MouseEvent | React.KeyboardEvent, item: IContextualMenuItem) => {
        onSubmitSublist(item.data as ListEntity, item.text);
        onDismiss(ev as React.MouseEvent<HTMLElement, MouseEvent>);
    };

    // A callback function for rendering the item icon based on the persist on click option
    const handleIconRender = (props: IContextualMenuItemProps, persistOnClick: boolean) => {
        const isChecked = isEntityChecked(props.item.data as Entity);

        if (persistOnClick) {
            return (
                <ToggleCheckBoxIcon
                    isChecked={isChecked}
                    onClick={e => {
                        e.stopPropagation();
                        handleOnItemClick(e, props.item, false);
                    }}
                />
            );
        }

        return (
            <Icon
                iconName={isChecked ? 'CheckMark' : ''}
                data-automation-id="checkMarkIcon"
                ariaLabel={isChecked ? localize(messages.checked) : ''}
                className={checkMarkClassName}
            />
        );
    };

    // If the user clicks on label of depth 3 the entity dropdown should opens the cascading
    // menus to this depth using the item render ref.
    // There is a time out to mitigate the race conditions because the ref of a child item
    // may be defined before the parent menu is opened.
    const openCascadingMenus = (ref: IContextualMenuRenderItem, entity: Entity) => {
        const itemToOpenIndex = itemsToOpen.indexOf(entity.id);
        if (itemToOpenIndex !== -1) {
            setTimeout(() => {
                ref?.openSubMenu();
                // Once the sub menu is opened should prevent it from opening again
                // until user clicks on another label.
                itemsToOpen.splice(itemToOpenIndex, 1);
            }, 100);
        }
    };

    const baseEntityMapper = (entity: Entity): IContextualMenuItem => ({
            key: `${entity.id}`,
            text: entity.name,
            iconProps: {},
            onClick: handleOnItemClick,
            onRenderIcon: e => handleIconRender(e, false),
            secondaryText: `(${prettyPrintEntity(entity.typeId)})`,
            data: entity,
            itemProps: {
                styles: baseItemStyles
            },
            componentRef: ref => openCascadingMenus(ref, entity)
        });

    // A function that maps a passed-in entity of type "List" to a corresponding IContextualMenuItem.
    const listEntityMapper = (entity: Entity): IContextualMenuItem => {
        const { sublists } = entity as ListEntity;

        const getQueryNoMatchMenuItem = (text: string) => getNotFoundItem({
                text,
                onClick: (_, item) => onCreateSublist(entity.id.toString(), item.text),
                secondaryText: localize(messages.createNewSublist)
            });

        const items = sublists.length
            ? sublists.map(sublist => ({
                  key: `${sublist.id}`,
                  text: `${sublist.name}`,
                  data: entity,
                  onClick: handleSubEntityClick
              }))
            : [
                  getNotFoundItem({
                      text: '',
                      secondaryText: localize(messages.emptyListItemsPlaceholder)
                  })
              ];

        return {
            ...baseEntityMapper(entity),
            subMenuProps: {
                items,
                onRenderMenuList: (props, defaultRender) => handleMenuListRender({
                        props,
                        depth: 1,
                        defaultRender,
                        getQueryNoMatchMenuItem,
                        onSubmit: text => onCreateSublist(entity.id.toString(), text),
                        searchPlaceholder: localize(messages.sublistSearchPlaceholder)
                    }),
                styles: subMenuStyles,
                calloutProps: { finalHeight: 1 },
                directionalHint: DirectionalHint.rightTopEdge,
                alignTargetEdge: true
            }
        };
    };

    // A function that maps a passed-in ML entity to a corresponding IContextualMenuItem.
    const multiDepthEntityMapper = (entity: Entity, depth: number = 0): IContextualMenuItem => {
        let items = (entity as SimpleEntity).children?.map(e => multiDepthEntityMapper(e, depth + 1)) || [];
        const labeledItems = (entity as SimpleEntity).children?.filter(isEntityChecked) || [];

        if (labeledItems.length) {
            items = [...items, generateDivider(), actions.unLabelAction(labeledItems)];
        }
        const getQueryNoMatchMenuItem = (text: string) => (entity.typeId === ModelType.Hierarchical
                ? getNotFoundItem({
                      text: localize(messages.noItemsFound)
                  })
                : getNotFoundItem({
                      text,
                      onClick: (_, item) => {
                          onCreateChild(item.text, entity.id);
                      },
                      secondaryText: localize(messages.createNewChild)
                  }));

        items = items.length ? items : [generateDivider()];

        const enableSubMenu = depth < LuisConstants.ResourceLimits.EntityDepthLimit && entity.typeId !== ModelType.HierarchicalChild;

        return {
            ...baseEntityMapper(entity),
            onRenderIcon: e => handleIconRender(e, depth === 0 && entity.typeId === ModelType.Simple),
            secondaryText: ' ',
            split: true,
            subMenuProps: enableSubMenu && {
                items,
                onRenderMenuList: (props, defaultRender) => handleMenuListRender({
                        props,
                        defaultRender,
                        depth: depth + 1,
                        getQueryNoMatchMenuItem,
                        onSubmit: text => onCreateChild(text, entity.id),
                        searchPlaceholder: localize(messages.entitySearchPlaceholder)
                    }),
                styles: subMenuStyles,
                calloutProps: { finalHeight: 1 },
                directionalHint: DirectionalHint.rightTopEdge,
                alignTargetEdge: true
            }
        };
    };

    // A function that maps a passed-in entities array to IContextualMenuItem array.
    const menuEntitiesMapper = (entitiesList: Entity[]): IContextualMenuItem[] => entitiesList.map(entity => {
            switch (entity.typeId) {
                case ModelType.List:
                    return listEntityMapper(entity);
                case ModelType.Simple:
                case ModelType.Hierarchical:
                    return multiDepthEntityMapper(entity);
                case ModelType.Composite:
                default:
                    return baseEntityMapper(entity);
            }
        });

    // Filtering the incoming entities to only ML and list entities
    const filteredEntities = entities.filter(entity => isMachineLearned(entity.typeId) || entity.typeId === ModelType.List);

    return menuEntitiesMapper(filteredEntities);
};
